import { useState } from "react";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/dialog";
import { Button } from "@/components/ui/button/button";
import { Input } from "@/components/ui/input/input";

const BREAK_REASONS = [
  { slug: "tea", label: "Tea" },
  { slug: "lunch", label: "Lunch" },
  { slug: "personal-call", label: "Personal call" },
  { slug: "other", label: "Other" },
] as const;

const REASON_MAX = 100;

export const StartBreakDialog = ({
  open,
  onOpenChange,
  onStart,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStart: (reason: string) => void;
  isPending: boolean;
}) => {
  const [picked, setPicked] = useState<string | null>(null);
  const [other, setOther] = useState("");

  const reason =
    picked === "other"
      ? other.trim()
      : picked
      ? BREAK_REASONS.find((r) => r.slug === picked)?.label ?? ""
      : "";

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setPicked(null);
      setOther("");
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" data-testid="start-break-dialog">
        <DialogHeader>
          <DialogTitle>Take a break</DialogTitle>
          <DialogDescription>
            Optional: what kind of break? You can skip this and just start.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2">
          {BREAK_REASONS.map((r) => (
            <Button
              key={r.slug}
              type="button"
              size="sm"
              variant={picked === r.slug ? "default" : "outline"}
              onClick={() => setPicked(picked === r.slug ? null : r.slug)}
              data-testid={`break-reason-${r.slug}`}
            >
              {r.label}
            </Button>
          ))}
        </div>

        {picked === "other" && (
          <Input
            autoFocus
            placeholder="Reason (optional, max 100 characters)"
            maxLength={REASON_MAX}
            value={other}
            onChange={(e) => setOther(e.target.value)}
            data-testid="break-reason-other-input"
          />
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isPending}
            onClick={() => onStart(reason)}
            className="gap-2"
            data-testid="break-confirm-button"
          >
            {isPending && <Loader2 className="w-3 h-3 animate-spin" />}
            Start break
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
